/**
 * StepDocumentsReview — final step of the loan application form (Step 9 in PR #26).
 *
 * PR #27 rework: this step used to host the document upload UI. It no longer
 * does — uploads happen post-submission via the auto-generated checklist that
 * the borrower lands on in the ClientPortal. This step is now a clean review-
 * and-submit screen with:
 *   - per-section completion summary
 *   - loan summary (amount / term / properties / loan type)
 *   - pre-submit checklist
 *   - submission-copy email
 *   - submit CTA
 *
 * After Submit, LoanApplicationForm's handleSubmit redirects the borrower to
 * /portal/applications/{id} where their freshly-generated doc-request
 * checklist is waiting.
 */
import {
  FileText,
  CheckCircle,
  AlertCircle,
  Building2,
  Users,
  Wallet,
  Home,
  DollarSign,
  PenTool,
  Briefcase,
  Loader2,
  Send,
  ClipboardList,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SelfVerifyPanel from "@/components/steps/SelfVerifyPanel";  // AML-Sumsub-2 (gated)

const LOAN_TYPE_LABELS = {
  commercial_loan: "Commercial",
  residential_loan: "Residential",
  residential_investment_loan: "Residential Investment",
  bridging_loan: "Bridging",
  business_loan: "Business Loan",
  construction_finance: "Construction Finance",
  residual_stock_loan: "Residual Stock Loan",
  mezzanine_loan: "Mezzanine",
  equity_loan: "Equity",
  industrial_loan: "Industrial",
  agricultural_loan: "Agricultural",
};

export const StepDocumentsReview = ({ formData, updateFormData, onSubmit, isLoading, API }) => {
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
    }).format(value || 0);

  const loanType = formData.loan_details?.loan_type || "";
  const loanTypeLabel = LOAN_TYPE_LABELS[loanType] || loanType || "Not set";

  const completionStatus = [
    {
      name: "Loan Type",
      icon: Briefcase,
      complete: !!loanType,
      details: loanTypeLabel,
    },
    {
      name: "Company Details",
      icon: Building2,
      complete: !!formData.company_borrower.company_name && !!formData.company_borrower.acn_abn,
      details: formData.company_borrower.company_name || "Not provided",
    },
    {
      name: "Guarantors",
      icon: Users,
      complete:
        formData.guarantors.length > 0 &&
        formData.guarantors.some((g) => g.given_names && g.surname),
      details: `${formData.guarantors.filter((g) => g.given_names).length} guarantor(s) added`,
    },
    {
      name: "Assets & Liabilities",
      icon: Wallet,
      complete:
        formData.company_assets_liabilities.some((a) => a.value > 0 || a.amount_owing > 0) ||
        formData.guarantor_assets_liabilities.some((a) => a.value > 0 || a.amount_owing > 0),
      details: "Financial position recorded",
    },
    {
      name: "Security Properties",
      icon: Home,
      // APP-FORM-FIX follow-up — StepSecurityDetails lets clients declare
      // no_security_property (and soft-prompts address as optional). Business /
      // commercial products also offer an unsecured path. The review gate must
      // honour those, otherwise Submit stays disabled after a valid skip.
      complete: (() => {
        if (formData.no_security_property) return true;
        if (formData.properties.some((p) => (p.address || "").trim())) return true;
        return loanType === "business_loan" || loanType === "commercial_loan";
      })(),
      details: formData.no_security_property
        ? "No security property declared"
        : formData.properties.some((p) => (p.address || "").trim())
          ? `${formData.properties.filter((p) => (p.address || "").trim()).length} property(ies) added`
          : loanType === "business_loan" || loanType === "commercial_loan"
            ? "Not required for this loan type"
            : "No property address added",
    },
    {
      name: "Loan Details",
      icon: DollarSign,
      complete: formData.loan_details.net_loan_required > 0,
      details: formatCurrency(formData.loan_details.net_loan_required),
    },
    {
      name: "Declarations",
      icon: PenTool,
      complete:
        formData.declaration_signatures.borrower_signatures.some((s) => s.signature_data) &&
        (formData.guarantors.length === 0 ||
          formData.declaration_signatures.guarantor_signatures.some((s) => s.signature_data)),
      details: "Signatures captured",
    },
  ];

  const allComplete = completionStatus.every((s) => s.complete);

  return (
    <div className="space-y-10">
      {/* AML-Sumsub-2 — optional identity verification. Renders nothing unless
          Sumsub is configured, so the live apply flow is unchanged until then. */}
      <SelfVerifyPanel API={API} />
      {/* What happens next callout — replaces the old upload zone */}
      <section className="form-card rounded">
        <h3 className="section-subheader flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#bd9a59]" />
          What happens after you submit
        </h3>
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-4 text-sm text-blue-900 space-y-2">
          <p>
            <strong>You don't need to upload anything here.</strong> When you click <em>Submit Application</em>,
            we'll generate a personalised document checklist tailored to your loan type
            (<strong>{loanTypeLabel}</strong>).
          </p>
          <p>
            You'll be taken straight to your <strong>Document Checklist</strong> page where you can upload
            each required document at your own pace. Solara reviews and accepts each document as it arrives —
            no need to gather everything before submitting.
          </p>
          <p className="text-xs text-blue-800/80">
            Files up to 250&nbsp;MB are supported. You can return to your checklist anytime by signing back in.
          </p>
        </div>
      </section>

      {/* Application Summary */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Application Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {completionStatus.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className={`p-4 rounded border ${
                  section.complete
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}
                data-testid={`summary-section-${index}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded ${section.complete ? "bg-green-100" : "bg-yellow-100"}`}>
                    <Icon
                      className={`w-4 h-4 ${
                        section.complete ? "text-green-600" : "text-yellow-600"
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-800">{section.name}</span>
                      {section.complete ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{section.details}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Loan Summary */}
      <section className="form-card rounded bg-[#021d49] text-white">
        <h3 className="text-xl font-heading font-semibold mb-6 text-white">Loan Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="p-4 bg-[#052b66] rounded">
            <p className="text-sm text-gray-300 mb-1">Loan Type</p>
            <p className="text-base font-bold text-[#bd9a59]" data-testid="summary-loan-type">
              {loanTypeLabel}
            </p>
          </div>
          <div className="p-4 bg-[#052b66] rounded">
            <p className="text-sm text-gray-300 mb-1">Net Loan Required</p>
            <p className="text-2xl font-bold text-[#bd9a59]" data-testid="summary-loan-amount">
              {formatCurrency(formData.loan_details.net_loan_required)}
            </p>
          </div>
          <div className="p-4 bg-[#052b66] rounded">
            <p className="text-sm text-gray-300 mb-1">Term</p>
            <p className="text-2xl font-bold text-white" data-testid="summary-loan-term">
              {formData.loan_details.term_months || 0} months
            </p>
          </div>
          <div className="p-4 bg-[#052b66] rounded">
            <p className="text-sm text-gray-300 mb-1">Properties as Security</p>
            <p className="text-2xl font-bold text-white" data-testid="summary-property-count">
              {formData.no_security_property
                ? "None"
                : formData.properties.filter((p) => (p.address || "").trim()).length}
            </p>
          </div>
        </div>

        {formData.loan_details.loan_purposes?.length > 0 && (
          <div className="mt-6 p-4 bg-[#052b66] rounded">
            <p className="text-sm text-gray-300 mb-2">Loan Purpose</p>
            <div className="flex flex-wrap gap-2">
              {formData.loan_details.loan_purposes.map((purpose, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#bd9a59] text-white text-sm rounded-full"
                >
                  {purpose}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Submit Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Submit Application</h3>

        {!allComplete ? (
          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Incomplete Application</p>
              <p className="text-sm text-yellow-700 mt-1">
                Please complete all required sections before submitting. Review the summary above to
                see which sections need attention.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded mb-6 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">Ready to Submit</p>
              <p className="text-sm text-green-700 mt-1">
                All required sections complete. Click <em>Submit</em> below — we'll generate your document
                checklist and take you straight to it.
              </p>
            </div>
          </div>
        )}

        <div className="p-4 bg-gray-50 border border-gray-200 rounded mb-6 space-y-3" data-testid="pre-submit-checklist-banner">
          <h4 className="text-sm font-semibold text-[#021d49]">Pre-Submit Checklist</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className={`rounded-lg px-3 py-2 border ${formData.declarations?.confirmed_true_correct ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <p className="text-xs uppercase tracking-wide opacity-70">Declarations</p>
              <p className="font-medium mt-1">
                {formData.declarations?.confirmed_true_correct ? "Complete" : "Needs attention"}
              </p>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${formData.declaration_signatures?.borrower_signatures?.some((s) => s.signature_data) ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <p className="text-xs uppercase tracking-wide opacity-70">Signatures</p>
              <p className="font-medium mt-1">
                {formData.declaration_signatures?.borrower_signatures?.some((s) => s.signature_data) ? "Saved" : "Missing"}
              </p>
            </div>
            <div className={`rounded-lg px-3 py-2 border ${loanType ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <p className="text-xs uppercase tracking-wide opacity-70">Loan Type</p>
              <p className="font-medium mt-1">{loanTypeLabel}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <Label htmlFor="submission-copy-email" className="form-label">
            Send me a copy of this submission (optional)
          </Label>
          <Input
            id="submission-copy-email"
            type="email"
            value={formData.submission_copy_email || ""}
            onChange={(e) => updateFormData("submission_copy_email", e.target.value)}
            placeholder="name@example.com"
            data-testid="submission-copy-email"
          />
          <p className="text-xs text-gray-500 mt-2">
            If entered, we'll email a copy of your submitted loan application summary to this address.
          </p>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          By submitting this application, you confirm that all information provided is true and accurate
          to the best of your knowledge. Solara Global will review your application and contact you
          regarding the next steps.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onSubmit}
            disabled={isLoading || !allComplete}
            className={`flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded font-medium text-lg transition-all ${
              allComplete ? "btn-accent hover:shadow-lg" : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            data-testid="submit-application-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Application
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          For assistance, contact us at{" "}
          <a href="mailto:loans@solaraglobal.com.au" className="text-[#bd9a59] hover:underline">
            loans@solaraglobal.com.au
          </a>
        </p>
      </section>
    </div>
  );
};
